/*
  TOSSIT homepage motion
  ======================
  Reveals elements marked with `data-toss` once they scroll into view.

  Design notes:
    - Content is visible until this file runs. `toss-ready` is added to
      <html> as the very first thing, so if the script 404s or throws,
      nothing is ever left invisible.
    - Each element is revealed once and then unobserved. There is no
      scroll handler and no re-hiding on scroll-up.
    - Re-initialises on `shopify:section:load` so sections re-rendered by
      the theme editor don't sit blank.
    - Elements above the fold use `data-toss-now`, which reveals straight
      away instead of waiting for an intersection. Nothing here blocks
      rendering, so LCP is unaffected.
*/
(function () {
  'use strict';

  /*
    Each homepage section loads this file with its own <script> tag so
    that any section works standalone. The browser fetches the asset
    once, but every tag executes it - without this guard the homepage
    would run eight copies: eight observers, eight visibilitychange
    listeners, eight editor hooks.
  */
  if (window.__tossMotion) return;
  window.__tossMotion = true;

  var READY = 'toss-ready';
  var IN = 'is-in';
  var DONE = 'is-done';

  // Hide-once-JS-is-confirmed. Anything after this point may throw
  // without leaving the page blank, because the reveal fallback below
  // strips the class again if we cannot observe.
  document.documentElement.classList.add(READY);

  var supported = 'IntersectionObserver' in window;

  if (!supported) {
    // No observer: show everything immediately rather than degrade.
    document.documentElement.classList.remove(READY);
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      // Trigger slightly before the element is fully on screen so the
      // move finishes about when it reaches comfortable reading position.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12,
    }
  );

  function reveal(el) {
    el.classList.add(IN);
    // Release the compositor layer once the move is finished.
    var ms = 1400;
    window.setTimeout(function () {
      el.classList.add(DONE);
    }, ms);
  }

  /*
    Split a heading into per-word masks.

    Walks only direct children: text nodes are split on whitespace, while
    element children (e.g. a <strong> highlight) are kept intact as a
    single unit. Splitting the innerHTML string instead would tear markup
    apart and drop the highlight styling.
  */
  function splitWords(host) {
    if (!host || host.dataset.tossSplit === 'done') return [];
    var nodes = Array.prototype.slice.call(host.childNodes);
    var frag = document.createDocumentFragment();
    var words = [];

    function wrap(content) {
      var mask = document.createElement('span');
      mask.className = 'toss-word';
      var inner = document.createElement('span');
      inner.setAttribute('data-toss', 'word');
      if (typeof content === 'string') {
        inner.textContent = content;
      } else {
        inner.appendChild(content);
      }
      mask.appendChild(inner);
      words.push(inner);
      return mask;
    }

    nodes.forEach(function (node) {
      if (node.nodeType === 3) {
        var parts = node.textContent.split(/(\s+)/);
        parts.forEach(function (part) {
          if (part === '') return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(' '));
          } else {
            frag.appendChild(wrap(part));
          }
        });
      } else if (node.nodeType === 1) {
        frag.appendChild(wrap(node));
      }
    });

    host.innerHTML = '';
    host.appendChild(frag);
    host.dataset.tossSplit = 'done';
    host.classList.add('toss-words');
    return words;
  }

  function prepDraw(el) {
    // stroke-dashoffset needs the real path length, which is only known
    // at runtime.
    try {
      var len = Math.ceil(el.getTotalLength());
      if (len) el.style.setProperty('--toss-len', len);
    } catch (e) {
      /* not a path - leave the CSS fallback length in place */
    }
  }

  function init(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[data-toss-section]');

    sections.forEach(function (section) {
      if (section.dataset.tossInit === 'done') return;
      section.dataset.tossInit = 'done';

      // Build word masks before collecting targets, so the generated
      // spans get observed too.
      section.querySelectorAll('[data-toss-words]').forEach(splitWords);

      // Assign stagger indexes per group.
      var groups = {};
      section.querySelectorAll('[data-toss-group]').forEach(function (el) {
        var name = el.getAttribute('data-toss-group');
        groups[name] = groups[name] || 0;
        el.style.setProperty('--toss-i', groups[name]);
        groups[name] += 1;
      });

      section.querySelectorAll('[data-toss="draw"]').forEach(prepDraw);

      var targets = section.querySelectorAll('[data-toss], [data-toss-drift]');

      targets.forEach(function (el) {
        if (el.dataset.tossBound === 'done') return;
        el.dataset.tossBound = 'done';

        if (el.hasAttribute('data-toss-now')) {
          /*
            Above the fold: reveal immediately rather than waiting for an
            intersection that has already happened.

            Deliberately NOT requestAnimationFrame. rAF is paused in
            background tabs, so a visitor who cmd-clicks the homepage or
            restores a session would land on a hero stuck at opacity 0
            until they focused the tab. Reading offsetWidth flushes the
            hidden state to the style system so the transition still runs
            from it, and works regardless of tab visibility.
          */
          void el.offsetWidth;
          reveal(el);
          return;
        }

        observer.observe(el);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /*
    IntersectionObserver delivery can be throttled while a tab is hidden.
    When the page becomes visible, sweep anything already on screen so no
    element is left invisible waiting for a callback that was skipped.
  */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    document.querySelectorAll('[data-toss]:not(.' + IN + ')').forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        reveal(el);
        observer.unobserve(el);
      }
    });
  });

  // Theme editor re-renders a section's markup wholesale; without this
  // the new nodes keep the hidden state and never reveal.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
